import "../JSONTools/JSONformatter.css";
import ToolHeader from "../../Components/ToolHeader";
import "./TextUtilities.css";
import { Row, Col } from "react-bootstrap";
import { useEffect, useMemo, useRef, useState } from "react";
import { RegexTestAPI } from "../../Services/APIService";

const flagOptions = [
    { key: "g", label: "global" },
    { key: "i", label: "ignore case" },
    { key: "m", label: "multiline" },
    { key: "s", label: "dotall" },
    { key: "u", label: "unicode" },
    { key: "y", label: "sticky" }
];

const cheatSections = [
    {
        title: "CHARACTER CLASSES",
        items: [
            ["[.]", "Any character except newline"],
            ["\\w \\s", "Word, digit, whitespace"],
            ["\\W \\S", "Not word, digit, whitespace"],
            ["[abc]", "Any of a, b, or c"],
            ["[^abc]", "Not a, b, or c"],
            ["[a-g]", "Character between a to g"]
        ]
    },
    {
        title: "ANCHORS",
        items: [
            ["^abc$", "Start / end of the string"],
            ["\\b \\B", "Word, not-word boundary"]
        ]
    },
    {
        title: "ESCAPED CHARACTERS",
        items: [
            ["\\. \\* \\\\", "Escaped special characters"],
            ["\\t \\n \\r", "Tab, linefeed, carriage return"]
        ]
    },
    {
        title: "GROUPS & LOOKAROUND",
        items: [
            ["(abc)", "Capture group"],
            ["\\1", "Backreference to group #1"],
            ["(?:abc)", "Non-capturing group"],
            ["(?=abc)", "Positive lookahead"],
            ["(?!abc)", "Negative lookahead"]
        ]
    },
    {
        title: "QUANTIFIERS & ALTERNATION",
        items: [
            ["a* a+ a?", "0 or more, 1 or more, 0 or 1"],
            ["a{5} a{2,}", "Exactly five, two or more"],
            ["a{1,3}", "Between one and three"],
            ["a+?", "Match as few as possible"],
            ["ab|cd", "Match ab or cd"]
        ]
    }
];

function RegexTester() {
    const [pattern, setPattern] = useState("[\\w.+-]+@[\\w.-]+\\.\\w+");
    const [testString, setTestString] = useState("Reach me at hello@example.com or support@lovable.dev for questions.\nAlso try admin@test.com - thanks!");
    const [flags, setFlags] = useState(["g"]);
    const [matches, setMatches] = useState([]);
    const [matchCount, setMatchCount] = useState(0);
    const [processingTimeMs, setProcessingTimeMs] = useState(0);
    const [apiError, setApiError] = useState("");
    const [activeResultTab, setActiveResultTab] = useState("matches");
    const [replacement, setReplacement] = useState("[redacted]");
    const regexRequestId = useRef(0);

    const flagText = flags.join("");

    const regexStatus = useMemo(() => {
        if (!pattern) {
            return { valid: true, message: "" };
        }

        try {
            // eslint-disable-next-line no-new
            new RegExp(pattern, flagText);
            return { valid: true, message: "Valid regex" };
        } catch (error) {
            return { valid: false, message: error.message };
        }
    }, [flagText, pattern]);

    useEffect(() => {
        const requestId = regexRequestId.current + 1;
        regexRequestId.current = requestId;

        if (!testString || !pattern || !regexStatus.valid) {
            setMatches([]);
            setMatchCount(0);
            setProcessingTimeMs(0);
            setApiError("");
            return;
        }

        const timeoutId = setTimeout(async () => {
            try {
                const response = await RegexTestAPI({
                    input: testString,
                    pattern
                });

                if (requestId === regexRequestId.current) {
                    setMatches(response.data.matches || []);
                    setMatchCount(response.data.matchCount || 0);
                    setProcessingTimeMs(response.data.processingTimeMs || 0);
                    setApiError("");
                }
            } catch (error) {
                if (requestId === regexRequestId.current) {
                    setApiError(error.response?.data?.message || error.message);
                    setMatches([]);
                    setMatchCount(0);
                    setProcessingTimeMs(0);
                }
            }
        }, 250);

        return () => clearTimeout(timeoutId);
    }, [pattern, regexStatus.valid, testString]);

    const highlightedPreview = useMemo(() => {
        if (!matches.length) {
            return testString;
        }

        const chunks = [];
        let cursor = 0;

        matches.forEach((match, index) => {
            if (match.start > cursor) {
                chunks.push(testString.slice(cursor, match.start));
            }

            chunks.push(
                <mark className="regex-preview-mark" key={`${match.start}-${match.end}-${index}`}>
                    {testString.slice(match.start, match.end)}
                </mark>
            );

            cursor = match.end;
        });

        if (cursor < testString.length) {
            chunks.push(testString.slice(cursor));
        }

        return chunks;
    }, [matches, testString]);

    const replacementOutput = useMemo(() => {
        if (!matches.length) {
            return testString;
        }

        let output = "";
        let cursor = 0;

        matches.forEach((match) => {
            output += testString.slice(cursor, match.start);
            output += replacement;
            cursor = match.end;
        });

        return output + testString.slice(cursor);
    }, [matches, replacement, testString]);

    const toggleFlag = (flag) => {
        setFlags((current) => (
            current.includes(flag)
                ? current.filter((item) => item !== flag)
                : [...current, flag]
        ));
    };

    return (
        <Col>
            <ToolHeader title="Regex Tester" description="Build, test, and reference regular expressions" showFavourite={false} />

            <Row className='Formatter regex-tester-layout'>
                <Col xs={12} lg={8} className="regex-main-col">
                    <section className="regex-card regex-expression-card">
                        <div className="regex-card-label">Regular Expression</div>
                        <div className="regex-expression-row">
                            <span className="regex-slash">/</span>
                            <input
                                value={pattern}
                                onChange={(event) => setPattern(event.target.value)}
                                className="regex-pattern-input"
                                spellCheck="false"
                            />
                            <span className="regex-slash">/</span>
                            <input
                                value={flagText}
                                readOnly
                                className="regex-flags-input"
                                aria-label="Active regex flags"
                            />
                            <button
                                type="button"
                                className="regex-icon-button"
                                aria-label="Copy regex"
                                onClick={() => navigator.clipboard.writeText(`/${pattern}/${flagText}`)}
                            >
                                Copy
                            </button>
                        </div>

                        <div className="regex-flag-row">
                            {flagOptions.map((flag) => (
                                <button
                                    key={flag.key}
                                    type="button"
                                    className={`regex-flag-chip ${flags.includes(flag.key) ? "active" : ""}`}
                                    onClick={() => toggleFlag(flag.key)}
                                >
                                    {flag.key} {flag.label}
                                </button>
                            ))}
                        </div>
                    </section>

                    <section className="regex-card regex-test-card">
                        <div className="regex-test-header">
                            <span>Test String</span>
                            <button
                                type="button"
                                className="regex-clear-button"
                                onClick={() => setTestString("")}
                            >
                                Clear
                            </button>
                        </div>
                        <textarea
                            value={testString}
                            onChange={(event) => setTestString(event.target.value)}
                            placeholder="Insert your test string here..."
                            className="regex-test-textarea"
                            spellCheck="false"
                        />
                    </section>

                    <div className={`regex-status ${regexStatus.valid ? "valid" : "invalid"}`}>
                        {apiError || regexStatus.message}
                    </div>

                    <section className="regex-card regex-preview-card">
                        <div className="regex-card-label">Highlighted Preview</div>
                        <pre className="regex-preview-box">{highlightedPreview}</pre>
                    </section>

                    <section className="regex-card regex-results-card">
                        <div className="regex-result-tabs">
                            {[
                                { key: "matches", label: `Matches (${matchCount})` },
                                { key: "replace", label: "Replace" },
                                { key: "info", label: "Match info" }
                            ].map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    className={`regex-result-tab ${activeResultTab === tab.key ? "active" : ""}`}
                                    onClick={() => setActiveResultTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {activeResultTab === "matches" && (
                            <div className="regex-match-list">
                                {matches.length ? matches.map((match, index) => (
                                    <div className="regex-match-card" key={`${match.start}-${match.end}-${index}`}>
                                        <span>#{index + 1}</span>
                                        <div>
                                            <strong>{match.value}</strong>
                                            <small>index {match.start} · length {match.end - match.start}</small>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => navigator.clipboard.writeText(match.value)}
                                        >
                                            Copy
                                        </button>
                                    </div>
                                )) : (
                                    <div className="regex-empty-state">No matches</div>
                                )}
                            </div>
                        )}

                        {activeResultTab === "replace" && (
                            <div className="regex-replace-view">
                                <label>
                                    Replacement (use $1, $2, $& matches)
                                    <input
                                        value={replacement}
                                        onChange={(event) => setReplacement(event.target.value)}
                                    />
                                </label>
                                <div className="regex-result-subhead">
                                    <span>Result</span>
                                    <button
                                        type="button"
                                        onClick={() => navigator.clipboard.writeText(replacementOutput)}
                                    >
                                        Copy
                                    </button>
                                </div>
                                <pre className="regex-replace-output">{replacementOutput}</pre>
                            </div>
                        )}

                        {activeResultTab === "info" && (
                            <div className="regex-info-list">
                                {matches.length ? matches.map((match, index) => (
                                    <div className="regex-info-card" key={`${match.start}-${match.end}-${index}`}>
                                        <span>Match #{index + 1}</span>
                                        <strong>{match.value}</strong>
                                    </div>
                                )) : (
                                    <div className="regex-empty-state">No match info</div>
                                )}
                            </div>
                        )}

                        <div className="regex-processing-time">
                            API time: {processingTimeMs} ms
                        </div>
                    </section>
                </Col>

                <Col xs={12} lg={4} className="regex-cheatsheet-col">
                    <aside className="regex-cheatsheet">
                        <div className="regex-cheatsheet-header">
                            <strong>Cheatsheet</strong>
                            <span>Common regex tokens & syntax</span>
                        </div>

                        {cheatSections.map((section) => (
                            <div className="regex-cheat-section" key={section.title}>
                                <div className="regex-cheat-title">{section.title}</div>
                                {section.items.map(([token, description]) => (
                                    <div className="regex-cheat-row" key={`${section.title}-${token}`}>
                                        <code>{token}</code>
                                        <span>{description}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </aside>
                </Col>
            </Row>
        </Col>
    );
}

export default RegexTester;
